// ─── src/utils/countrySlug.js ──────────────────────────────────────────────────
// Single source of truth for country display-name → URL slug resolution.
// Consumed by SlimHero, ImmersiveSearch, and GlobalSearchBar so all three bars
// use identical alias rules and can never diverge.
import { LOCATIONS }          from "../data/globalVendors";
import { COUNTRIES as GEO_COUNTRIES } from "../data/geo";

// Known display-name mismatches + countries not yet in GEO_COUNTRIES.
// GEO_COUNTRIES only has 14 active markets; CURATED_DESTINATIONS covers 28+.
// Any country whose name → slug is non-trivial OR that isn't in GEO_COUNTRIES
// must be listed here so resolveCountrySlug never silently returns null.
const DISPLAY_OVERRIDES = {
  // Display-name mismatches (official name ≠ GEO_COUNTRIES slug)
  "United Kingdom":           "england",
  "UK":                       "england",
  "Scotland":                 "scotland",
  "United Arab Emirates":     "uae",
  "UAE":                      "uae",
  "United States":            "usa",
  "United States of America": "usa",

  // Countries in CURATED_DESTINATIONS not yet in GEO_COUNTRIES.
  // Slug is lowercase + hyphens, ready to route when geo pages are built.
  "Switzerland":       "switzerland",
  "Spain":             "spain",
  "Ireland":           "ireland",
  "Morocco":           "morocco",
  "Slovenia":          "slovenia",
  "Hungary":           "hungary",
  "Portugal":          "portugal",
  "Mexico":            "mexico",
  "Costa Rica":        "costa-rica",
  "Malaysia":          "malaysia",
  "Thailand":          "thailand",
  "Indonesia":         "indonesia",
  "India":             "india",
  "Sri Lanka":         "sri-lanka",
  "Japan":             "japan",
  "Maldives":          "maldives",
  "Seychelles":        "seychelles",
  "Oman":              "oman",
  "Qatar":             "qatar",
  "Kenya":             "kenya",
  "Tanzania":          "tanzania",
  "Australia":         "australia",
  "New Zealand":       "new-zealand",
  "Fiji":              "fiji",
  "French Polynesia":  "french-polynesia",
  "Turkey":            "turkey",
  "Denmark":           "denmark",
  "Saudi Arabia":      "saudi-arabia",
  "Greece":            "greece",
  "Cyprus":            "cyprus",
  "Malta":             "malta",
  "Montenegro":        "montenegro",
  "Bahamas":           "bahamas",
  "British Virgin Islands": "british-virgin-islands",
};

// Build alias map from LOCATIONS vs GEO_COUNTRIES (auto-covers exact matches).
const _map = {};
LOCATIONS.forEach((group) => {
  group.items?.forEach((loc) => {
    const match = GEO_COUNTRIES.find((c) => c.name === loc.country);
    if (match) _map[loc.country] = match.slug;
  });
});
Object.assign(_map, DISPLAY_OVERRIDES);

export const COUNTRY_NAME_TO_SLUG = _map;

// Resolve a country display name to its URL slug.
// Tries GEO_COUNTRIES direct match first (covers Italy, France, etc.),
// then falls back to the alias map for display-name mismatches.
export function resolveCountrySlug(name) {
  if (!name) return null;
  return GEO_COUNTRIES.find((c) => c.name === name)?.slug
    || _map[name]
    || null;
}
